import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  COLLAPSED_PANE_HEIGHT,
  PANE_DIVIDER_HEIGHT,
  X_AXIS_LABEL_HEIGHT,
} from '../constants';
import { getPaneHeights } from '../utils/pane-heights';

export interface UsePaneUIStateOptions {
  paneIds: string[];
  bodyRef: React.RefObject<HTMLDivElement | null>;
  onPaneCollapse?: (paneId: string, collapsed: boolean) => void;
  onPaneMaximize?: (paneId: string, maximized: boolean) => void;
}

export interface UsePaneUIStateResult {
  paneFractions: number[];
  paneHeights: number[];
  collapsedPanes: Set<string>;
  maximizedPaneId: string | null;
  toggleCollapsed: (paneId: string) => void;
  toggleMaximized: (paneId: string) => void;
  clearCollapsedFor: (paneId: string) => void;
  setMaximizedIfMatch: (paneId: string) => void;
  swapPaneUiState: (aId: string, bId: string) => void;
  startDividerDrag: (
    index: number,
    event: React.PointerEvent<HTMLDivElement>
  ) => void;
}

/**
 * Per-pane UI state: collapse / maximize, fraction ratios for divider
 * drag, and v1-style pixel heights (collapsed strip + leftover split).
 */
export const usePaneUIState = ({
  paneIds,
  bodyRef,
  onPaneCollapse,
  onPaneMaximize,
}: UsePaneUIStateOptions): UsePaneUIStateResult => {
  const paneCount = paneIds.length;
  const [paneFractions, setPaneFractions] = useState<number[]>([]);
  const [collapsedPanes, setCollapsedPanes] = useState<Set<string>>(
    () => new Set()
  );
  const [maximizedPaneId, setMaximizedPaneId] = useState<string | null>(null);
  const [containerHeight, setContainerHeight] = useState(0);

  useLayoutEffect(() => {
    const node = bodyRef.current;

    if (!node) {
      return undefined;
    }

    const updateHeight = () => {
      setContainerHeight(node.getBoundingClientRect().height);
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);

    observer.observe(node);
    return () => observer.disconnect();
  }, [bodyRef]);

  useEffect(() => {
    setPaneFractions(prev => {
      if (prev.length === paneCount) {
        return prev;
      }

      return Array.from(
        { length: paneCount },
        () => 1 / Math.max(paneCount, 1)
      );
    });
  }, [paneCount]);

  useEffect(() => {
    if (paneCount !== 1) {
      return;
    }

    setCollapsedPanes(prev => (prev.size === 0 ? prev : new Set()));
  }, [paneCount]);

  const toggleCollapsed = useCallback(
    (paneId: string) => {
      setCollapsedPanes(prev => {
        const next = new Set(prev);
        const willBeCollapsed = !next.has(paneId);

        if (willBeCollapsed) {
          const expandedIds = paneIds.filter(id => !prev.has(id));

          if (expandedIds.length <= 1 && expandedIds[0] === paneId) {
            return prev;
          }

          next.add(paneId);
        } else {
          next.delete(paneId);
        }

        onPaneCollapse?.(paneId, willBeCollapsed);
        return next;
      });
    },
    [onPaneCollapse, paneIds]
  );

  const toggleMaximized = useCallback(
    (paneId: string) => {
      const willMaximize = maximizedPaneId !== paneId;

      setMaximizedPaneId(willMaximize ? paneId : null);
      onPaneMaximize?.(paneId, willMaximize);

      if (!willMaximize) {
        return;
      }

      setCollapsedPanes(prev => {
        if (!prev.has(paneId)) {
          return prev;
        }

        const next = new Set(prev);

        next.delete(paneId);
        onPaneCollapse?.(paneId, false);
        return next;
      });
    },
    [maximizedPaneId, onPaneCollapse, onPaneMaximize]
  );

  const clearCollapsedFor = useCallback((paneId: string) => {
    setCollapsedPanes(prev => {
      if (!prev.has(paneId)) {
        return prev;
      }

      const next = new Set(prev);

      next.delete(paneId);
      return next;
    });
  }, []);

  const setMaximizedIfMatch = useCallback((paneId: string) => {
    setMaximizedPaneId(prev => (prev === paneId ? null : prev));
  }, []);

  const swapPaneUiState = useCallback(
    (aId: string, bId: string) => {
      if (aId === bId) {
        return;
      }

      setCollapsedPanes(prev => {
        const aCollapsed = prev.has(aId);
        const bCollapsed = prev.has(bId);

        if (aCollapsed === bCollapsed) {
          return prev;
        }

        const next = new Set(prev);

        if (bCollapsed) {
          next.add(aId);
        } else {
          next.delete(aId);
        }

        if (aCollapsed) {
          next.add(bId);
        } else {
          next.delete(bId);
        }

        onPaneCollapse?.(aId, bCollapsed);
        onPaneCollapse?.(bId, aCollapsed);
        return next;
      });

      setMaximizedPaneId(prev => {
        if (prev === aId) {
          onPaneMaximize?.(aId, false);
          onPaneMaximize?.(bId, true);
          return bId;
        }

        if (prev === bId) {
          onPaneMaximize?.(bId, false);
          onPaneMaximize?.(aId, true);
          return aId;
        }

        return prev;
      });

      setPaneFractions(prev => {
        const aIdx = paneIds.indexOf(aId);
        const bIdx = paneIds.indexOf(bId);

        if (
          aIdx < 0 ||
          bIdx < 0 ||
          aIdx >= prev.length ||
          bIdx >= prev.length
        ) {
          return prev;
        }

        const next = prev.slice();

        next[aIdx] = prev[bIdx];
        next[bIdx] = prev[aIdx];
        return next;
      });
    },
    [onPaneCollapse, onPaneMaximize, paneIds]
  );

  const paneHeights = useMemo(() => {
    const fractions =
      paneFractions.length === paneCount
        ? paneFractions
        : Array.from({ length: paneCount }, () => 1 / Math.max(paneCount, 1));

    return getPaneHeights({
      containerHeight,
      dividerHeight: PANE_DIVIDER_HEIGHT,
      collapsedHeight: COLLAPSED_PANE_HEIGHT,
      xAxisLabelHeight: X_AXIS_LABEL_HEIGHT,
      panes: paneIds.map((id, index) => ({
        fraction: fractions[index] ?? 0,
        collapsed: collapsedPanes.has(id),
        maximized: maximizedPaneId === id,
        hostsXAxis: index === paneCount - 1 && maximizedPaneId === null,
      })),
    });
  }, [
    containerHeight,
    paneCount,
    paneFractions,
    paneIds,
    collapsedPanes,
    maximizedPaneId,
  ]);

  const dividerDragRef = useRef<{
    index: number;
    startY: number;
    startFractions: number[];
    containerHeight: number;
  } | null>(null);

  const handleDividerPointerMove = useCallback((event: PointerEvent) => {
    const drag = dividerDragRef.current;

    if (!drag || drag.containerHeight <= 0) {
      return;
    }

    const deltaY = event.clientY - drag.startY;
    const deltaFraction = deltaY / drag.containerHeight;
    const { index, startFractions } = drag;
    const minFraction = COLLAPSED_PANE_HEIGHT / drag.containerHeight;
    const aBase = startFractions[index];
    const bBase = startFractions[index + 1];
    const maxDelta = bBase - minFraction;
    const minDelta = -(aBase - minFraction);
    const clamped = Math.min(Math.max(deltaFraction, minDelta), maxDelta);
    const next = startFractions.slice();

    next[index] = aBase + clamped;
    next[index + 1] = bBase - clamped;
    setPaneFractions(next);
  }, []);

  const handleDividerPointerUp = useCallback(() => {
    dividerDragRef.current = null;
    window.removeEventListener('pointermove', handleDividerPointerMove);
    window.removeEventListener('pointerup', handleDividerPointerUp);
  }, [handleDividerPointerMove]);

  const startDividerDrag = useCallback(
    (index: number, event: React.PointerEvent<HTMLDivElement>) => {
      const container = bodyRef.current;
      const upperId = paneIds[index];
      const lowerId = paneIds[index + 1];

      if (
        !container ||
        !upperId ||
        !lowerId ||
        collapsedPanes.has(upperId) ||
        collapsedPanes.has(lowerId)
      ) {
        return;
      }

      event.preventDefault();
      dividerDragRef.current = {
        index,
        startY: event.clientY,
        startFractions: paneFractions.slice(),
        containerHeight: container.getBoundingClientRect().height,
      };

      window.addEventListener('pointermove', handleDividerPointerMove);
      window.addEventListener('pointerup', handleDividerPointerUp);
    },
    [
      bodyRef,
      paneIds,
      collapsedPanes,
      paneFractions,
      handleDividerPointerMove,
      handleDividerPointerUp,
    ]
  );

  useEffect(
    () => () => {
      window.removeEventListener('pointermove', handleDividerPointerMove);
      window.removeEventListener('pointerup', handleDividerPointerUp);
    },
    [handleDividerPointerMove, handleDividerPointerUp]
  );

  return {
    paneFractions,
    paneHeights,
    collapsedPanes,
    maximizedPaneId,
    toggleCollapsed,
    toggleMaximized,
    clearCollapsedFor,
    setMaximizedIfMatch,
    swapPaneUiState,
    startDividerDrag,
  };
};
